import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const Container = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid hsl(var(--foreground) / 0.05);
`;

export const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  height: 64px;
  padding: 0 2rem;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 860px) {
    height: 74px;
    padding: 0 1rem;
    gap: 12px;
  }

  @media (max-width: 420px) {
    padding: 0 12px;
    gap: 10px;
  }
`;

export const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
  flex: 0 1 auto;
`;

export const Center = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 0;

  @media (max-width: 860px) {
    display: none;
  }
`;

export const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;

  font-family: 'Nunito', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  font-weight: 800;
  font-size: 20px;
  text-decoration: none;
  color: hsl(var(--foreground));

  white-space: nowrap;
  min-width: 0;

  .logoIcon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }

  .brandIcon {
    width: 42px;
    height: 42px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 6px 14px hsl(var(--primary) / 0.18));
    transition: transform 0.22s ease, filter 0.22s ease;
  }

  &:hover .brandIcon {
    transform: translateY(-1px) scale(1.02);
    filter: drop-shadow(0 10px 18px hsl(var(--primary) / 0.24));
  }

  @media (max-width: 860px) {
    gap: 6px;

    .brandIcon {
      width: 38px;
      height: 38px;
    }
  }

  @media (max-width: 420px) {
    font-size: 18px;
  }

  @media (max-width: 360px) {
    font-size: 16px;
  }
`;

export const LogoText = styled.span`
  letter-spacing: -0.03em;
  font-weight: 900;
  color: hsl(var(--foreground));
  margin-left: 10px;

  .cp {
    color: hsl(var(--primary));
  }

  body[data-theme='light'] & .cp {
    color: hsl(var(--primary));
  }

  @media (max-width: 860px) {
    display: block;
    margin-left: 2px;
    font-size: 18px;
  }

  @media (max-width: 360px) {
    font-size: 16px;
  }
`;

export const DesktopNav = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 860px) {
    display: none;
  }
`;

export const NavItem = styled(Link)<{ $active: boolean; $admin?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;

  font-weight: 600;
  text-decoration: none;
  transition: all 0.25s ease;

  color: ${({ $active }) =>
    $active ? 'hsl(var(--sky))' : 'hsl(var(--foreground) / 0.7)'};

  ${({ $active }) =>
    $active
      ? `
        text-shadow: 0 0 12px hsl(var(--sky) / 0.45);
      `
      : ''}

  ${({ $admin }) =>
    $admin
      ? `
        border: 1px solid hsl(var(--sky) / 0.16);
        background: hsl(var(--sky) / 0.05);
      `
      : `
        border: 1px solid transparent;
      `}

  &:hover {
    color: hsl(var(--foreground));
    background: hsl(var(--foreground) / 0.05);
  }

  span {
    display: inline-flex;
    align-items: center;
  }
`;

export const Indicator = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;

  height: 2px;
  border-radius: 999px;

  transition: transform 0.25s ease, width 0.25s ease, opacity 0.2s ease;
  will-change: transform, width;
  pointer-events: none;

  filter: drop-shadow(0 0 10px hsl(var(--sky) / 0.35));

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;

    height: 1px;
    border-radius: 999px;

    background: hsl(var(--foreground) / 0.55);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;

    height: 2px;
    border-radius: 999px;

    background: linear-gradient(90deg, hsl(var(--sky)), hsl(var(--primary)));
  }
`;

export const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  margin-left: auto;
  min-width: 0;

  .mobileActions {
    display: none;
  }

  @media (max-width: 980px) {
    .name {
      max-width: 120px;
    }
  }

  @media (max-width: 860px) {
    gap: 8px;

    .desktopOnly,
    a {
      display: none;
    }

    .mobileActions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
`;

export const GhostLink = styled(Link)`
  padding: 10px 12px;
  border-radius: 12px;
  text-decoration: none;

  color: hsl(var(--sky));
  font-weight: 800;

  border: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    background: hsl(var(--sky) / 0.1);
    border-color: hsl(var(--sky) / 0.2);
  }

  @media (max-width: 860px) {
    display: none;
  }
`;

export const PrimaryLink = styled(Link)`
  padding: 10px 14px;
  border-radius: 12px;
  text-decoration: none;

  font-weight: 800;
  color: hsl(var(--primary-foreground));
  background: linear-gradient(90deg, hsl(var(--primary)), hsl(var(--warm)));
  box-shadow: 0 12px 30px hsl(var(--primary) / 0.25);

  transition: transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
    box-shadow: 0 16px 36px hsl(var(--primary) / 0.3);
  }

  @media (max-width: 860px) {
    display: none;
  }
`;

export const UserPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  padding: 10px 12px;
  border-radius: 999px;

  border: 1px solid hsl(var(--foreground) / 0.08);
  background: hsl(var(--card) / 0.35);
  box-shadow: 0 10px 24px hsl(var(--background) / 0.08);

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: hsl(var(--primary));
    box-shadow: 0 0 16px hsl(var(--primary) / 0.35);
  }

  .name {
    font-weight: 900;
    font-size: 13px;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: hsl(var(--foreground));
  }

  body[data-theme='light'] & {
    background: linear-gradient(
      135deg,
      hsl(var(--primary) / 0.08),
      hsl(var(--card) / 0.95)
    );
    border-color: hsl(var(--primary) / 0.12);
  }
`;

export const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 12px;

  display: grid;
  place-items: center;

  border: 1px solid hsl(var(--foreground) / 0.08);
  background: hsl(var(--card) / 0.35);
  color: hsl(var(--foreground));
  cursor: pointer;

  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: hsl(var(--card) / 0.5);
    border-color: hsl(var(--foreground) / 0.14);
  }

  body[data-theme='light'] & {
    background: hsl(var(--card));
    box-shadow: 0 10px 24px hsl(var(--background) / 0.08);
  }

  &.mobile {
    display: none;
  }

  @media (max-width: 860px) {
    width: 44px;
    height: 44px;
    border-radius: 14px;

    &.mobile {
      display: grid;
    }
  }
`;

export const ThemeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  min-width: 42px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid hsl(var(--accent) / 0.18);
  background: radial-gradient(
      circle at top,
      hsl(var(--accent) / 0.12),
      transparent 35%
    ),
    linear-gradient(
      180deg,
      hsl(var(--card) / 0.82),
      hsl(var(--background) / 0.88)
    );
  color: hsl(var(--foreground));
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease,
    filter 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: hsl(var(--accent) / 0.35);
    box-shadow: 0 0 0 3px hsl(var(--accent) / 0.08),
      0 8px 24px hsl(var(--background) / 0.18);
    filter: brightness(1.03);
  }

  body[data-theme='light'] & {
    background: hsl(222 47% 11%);
    color: hsl(0 0% 100%);
    border-color: hsl(222 47% 11% / 0.08);
    box-shadow: 0 10px 24px hsl(222 47% 11% / 0.12);
  }

  body[data-theme='light'] &:hover {
    background: hsl(222 47% 15%);
    box-shadow: 0 12px 28px hsl(222 47% 11% / 0.16);
  }

  &.mobileTheme {
    display: none;
    min-width: 44px;
    height: 44px;
    padding: 0;
    border-radius: 14px;
  }

  @media (max-width: 860px) {
    &.desktopOnly {
      display: none;
    }

    &.mobileTheme {
      display: inline-flex;
    }
  }
`;

export const MobilePanel = styled.div`
  border-top: 1px solid hsl(var(--foreground) / 0.05);
  overflow: hidden;

  max-height: 0;
  opacity: 0;
  transition: max-height 0.3s ease, opacity 0.25s ease;

  &[data-open='true'] {
    max-height: 420px;
    opacity: 1;
  }

  @media (min-width: 861px) {
    display: none;
  }
`;

export const MobileNav = styled.nav`
  padding: 14px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const MobileNavItem = styled.a<{ $active: boolean; $admin?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 12px;
  border-radius: 12px;
  text-decoration: none;

  color: ${({ $active }) =>
    $active ? 'hsl(var(--sky))' : 'hsl(var(--foreground) / 0.7)'};

  font-weight: 800;
  background: ${({ $active, $admin }) => {
    if ($active) return 'hsl(var(--sky) / 0.1)';
    if ($admin) return 'hsl(var(--sky) / 0.05)';
    return 'transparent';
  }};

  ${({ $active }) =>
    $active
      ? `
        text-shadow: 0 0 12px hsl(var(--sky) / 0.45);
      `
      : ''}

  border: 1px solid
    ${({ $active, $admin }) => {
    if ($active) return 'hsl(var(--sky) / 0.2)';
    if ($admin) return 'hsl(var(--sky) / 0.12)';
    return 'transparent';
  }};

  cursor: pointer;
  text-align: left;

  &:hover {
    background: hsl(var(--foreground) / 0.05);
    color: hsl(var(--foreground));
  }

  span {
    display: inline-flex;
    align-items: center;
  }
`;

export const MobileActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 12px 12px;
  border-radius: 12px;
  text-align: left;
  cursor: pointer;

  color: hsl(var(--foreground) / 0.7);
  font-weight: 800;
  background: transparent;
  border: 1px solid transparent;

  &:hover {
    background: hsl(var(--foreground) / 0.05);
    color: hsl(var(--foreground));
  }

  span {
    display: inline-flex;
    align-items: center;
  }
`;

export const NeonLine = styled.div`
  height: 1px;
  margin: 10px 0;
  background: linear-gradient(
    90deg,
    hsl(var(--sky)),
    hsl(var(--primary)),
    hsl(var(--warm))
  );
  opacity: 0.55;
  border-radius: 999px;
`;

export const AdminBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid hsl(var(--sky) / 0.18);
  background: hsl(var(--sky) / 0.08);
  color: hsl(var(--sky));
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  &.mobileBadge {
    margin-left: auto;
  }
`;
